// Shared sessionStorage cache for the on-demand section renders below.
//
// The key carries everything that can change the markup: a hand-bumped schema
// version (for when the JS starts expecting a different shape), the theme id
// (so a deploy cannot keep serving the previous theme's markup to open tabs),
// and locale + country (the fetch URL is locale-prefixed and `money` output is
// market-bound, but neither used to be part of the key).
//
// TTL is per caller. Markup that only depends on the key may live for the whole
// session; anything embedding a price or a stock-dependent choice must not,
// because the Easy:Sale app rewrites compare_at_price on the storefront and a
// session can run for hours.
const sjCache = (() => {
  const SCHEMA = 1;
  const key = (name) => [
    'sj',
    name,
    `v${SCHEMA}`,
    window.Shopify?.theme?.id || 'x',
    window.Shopify?.locale || 'x',
    window.Shopify?.country || 'x',
  ].join(':');
  return {
    // ttl in ms; omit for session-lifetime.
    get(name, ttl) {
      try {
        const raw = sessionStorage.getItem(key(name));
        if (!raw) return null;
        const entry = JSON.parse(raw);
        if (typeof entry?.html !== 'string') return null;
        if (ttl && Date.now() - entry.t > ttl) { sessionStorage.removeItem(key(name)); return null; }
        return entry.html;
      } catch (e) { return null; }
    },
    set(name, html) {
      try { sessionStorage.setItem(key(name), JSON.stringify({ t: Date.now(), html })); }
      catch (e) { /* private mode / quota */ }
    },
  };
})();

// Mobile nav drawer. The drawer lives inside the sticky header section, so its
// top offset varies (promo bar visible vs scrolled away), measure the real
// offset when opening and cap the height to what fits, then lock body scroll
// so the offset can't change underneath the open drawer.
(() => {
  const sizeMobileNav = (nav) => {
    nav.style.maxHeight = `${window.innerHeight - nav.getBoundingClientRect().top}px`;
  };
  // The drawer body is rendered on demand (Section Rendering API), so its menu
  // walk + per-link collection lookups stay off the initial page render. The
  // markup is identical on every page, so cache it per session: one section
  // render per visit instead of one per page, and later pages open instantly.
  // No TTL: this is menu structure only, no price, no stock, no cart, so the
  // sjCache key (theme id + locale + country) is the only thing that can
  // invalidate it, and that is exactly right.
  const readNavCache = () => sjCache.get('mnav');
  const applyNavHtml = (nav, html) => {
    nav.innerHTML = html;
    nav.dataset.loaded = '1';
    nav.setAttribute('aria-busy', 'false');
  };
  // One in-flight request shared by every caller (prefetch, warm-up, open).
  let navRequest = null;
  const requestMobileNav = (nav) => {
    if (navRequest) return navRequest;
    navRequest = fetch(nav.dataset.src, { headers: { 'X-Requested-With': 'fetch' } })
      .then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then((text) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = text;
        const html = (tmp.querySelector('#shopify-section-mobile-nav') || tmp).innerHTML;
        sjCache.set('mnav', html);
        return html;
      })
      .catch((e) => { navRequest = null; throw e; });
    return navRequest;
  };
  // Skeleton stand-in for the drawer body:
  // one collapsed row (thumb + label + chevron) per top-level menu link, so the
  // drawer opens at roughly its final height and the fetched markup swaps in
  // without a jump. The row count comes from the header (data-rows), which knows
  // the menu size without paying for the menu walk.
  const navSkeletonHTML = (nav) => {
    const rows = Math.min(Math.max(parseInt(nav.dataset.rows, 10) || 6, 3), 12);
    return '<div class="mnav-skeleton" aria-hidden="true">' +
      ('<div class="mnav-sk-row">' +
         '<span class="mnav-sk-thumb"></span>' +
         '<span class="mnav-sk-line"></span>' +
         '<span class="mnav-sk-chev"></span>' +
       '</div>').repeat(rows) +
      '</div>';
  };
  // Only paint the placeholder when the drawer is on screen and still empty, a
  // prefetch into a closed drawer must not leave a skeleton behind, and an
  // open during an in-flight prefetch must not show a blank drawer.
  const showNavPlaceholder = (nav) => {
    if (nav.classList.contains('hidden') || nav.firstElementChild) return;
    nav.innerHTML = navSkeletonHTML(nav);
  };
  const fillMobileNav = (nav) => {
    if (nav.dataset.loaded === '1' || !nav.dataset.src) return;
    if (nav.dataset.loading === '1') { showNavPlaceholder(nav); return; }
    const cached = readNavCache();
    if (cached) { applyNavHtml(nav, cached); return; }
    nav.dataset.loading = '1';
    nav.setAttribute('aria-busy', 'true');
    showNavPlaceholder(nav);
    requestMobileNav(nav)
      .then((html) => { applyNavHtml(nav, html); })
      .catch(() => {
        // Let a later open retry, and fall back to a plain link to the shop.
        nav.innerHTML = '<a class="mnav-link" href="/collections/all">Alle producten</a>';
        nav.setAttribute('aria-busy', 'false');
      })
      .finally(() => { nav.dataset.loading = ''; });
  };
  // Prefetch on press: fires ~100ms before the click lands, so a cold drawer is
  // usually already filled by the time it opens.
  document.getElementById('mobile-nav-toggle')?.addEventListener('pointerdown', () => {
    const nav = document.getElementById('mobile-nav');
    if (nav) fillMobileNav(nav);
  }, { passive: true });
  // Warm the session cache once the page is idle, and only where the drawer can
  // actually be used, desktop never pays for it, and it never competes with LCP.
  const warmMobileNav = () => {
    if (!window.matchMedia('(max-width: 1023px)').matches) return;
    const nav = document.getElementById('mobile-nav');
    if (nav) fillMobileNav(nav);
  };
  const scheduleWarm = () => {
    if (window.requestIdleCallback) window.requestIdleCallback(warmMobileNav, { timeout: 3000 });
    else window.setTimeout(warmMobileNav, 1200);
  };
  if (document.readyState === 'complete') scheduleWarm();
  else window.addEventListener('load', scheduleWarm, { once: true });
  document.getElementById('mobile-nav-toggle')?.addEventListener('click', (e) => {
    const nav = document.getElementById('mobile-nav');
    if (!nav) return;
    // Toggle synchronously: awaiting the fetch first would leave the drawer shut
    // during the request, so a second click would flip it open and the resolved
    // first click would flip it straight back shut.
    const open = !nav.classList.toggle('hidden');
    e.currentTarget.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.body.classList.toggle('mnav-lock', open);
    if (open) {
      sizeMobileNav(nav);
      // Nav and search popup are mutually exclusive below the header.
      closeMobileSearch();
      fillMobileNav(nav);
    }
  });
  let navResizeRaf = false;
  window.addEventListener('resize', () => {
    if (navResizeRaf) return;
    navResizeRaf = true;
    requestAnimationFrame(() => {
      navResizeRaf = false;
      const nav = document.getElementById('mobile-nav');
      if (nav && !nav.classList.contains('hidden')) sizeMobileNav(nav);
    });
  });
})();

// Mobile search popup: same live search as desktop, dropped in below the header.
function closeMobileSearch() {
  const panel = document.getElementById('mobile-search');
  if (!panel || panel.classList.contains('hidden')) return;
  panel.classList.add('hidden');
  document.getElementById('mobile-search-toggle')?.setAttribute('aria-expanded', 'false');
}
document.getElementById('mobile-search-toggle')?.addEventListener('click', (e) => {
  const panel = document.getElementById('mobile-search');
  if (!panel) return;
  const open = !panel.classList.toggle('hidden');
  e.currentTarget.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (open) {
    // Close the nav drawer so the popup sits directly under the header.
    const nav = document.getElementById('mobile-nav');
    if (nav && !nav.classList.contains('hidden')) {
      nav.classList.add('hidden');
      document.getElementById('mobile-nav-toggle')?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('mnav-lock');
    }
    panel.querySelector('input[type="search"]')?.focus();
  }
});
document.getElementById('mobile-search')?.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeMobileSearch();
});

// Promo bar dismiss (persists via localStorage, mirrors storefront).
(() => {
  const bar = document.getElementById('promo-bar');
  if (!bar) return;
  if (localStorage.getItem('sj.promo') === 'off') { bar.remove(); return; }
  document.getElementById('promo-close')?.addEventListener('click', () => {
    localStorage.setItem('sj.promo', 'off');
    bar.remove();
  });
})();

// Typewriter placeholder in the header search (storefront useTypedPlaceholder).
(() => {
  const input = document.querySelector('[data-typed-search]');
  if (!input) return;
  const terms = [
    'glijmiddel', 'vibrators', 'bondage sets', 'kanten lingerie',
    'bdsm accessoires', 'massageolie', 'ondeugende setjes',
  ];
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    input.placeholder = `Zoek naar ${terms[0]}…`;
    return;
  }
  let ti = 0, ci = 0, deleting = false, active = true, timer;
  const tick = () => {
    if (!active) return;
    const w = terms[ti];
    if (!deleting) {
      ci++;
      input.placeholder = `Zoek naar ${w.slice(0, ci)}…`;
      if (ci === w.length) { deleting = true; timer = setTimeout(tick, 1600); return; }
      timer = setTimeout(tick, 80);
    } else {
      ci--;
      input.placeholder = `Zoek naar ${w.slice(0, ci)}…`;
      if (ci === 0) { deleting = false; ti = (ti + 1) % terms.length; timer = setTimeout(tick, 350); return; }
      timer = setTimeout(tick, 40);
    }
  };
  const start = () => { if (input.value) return; active = true; clearTimeout(timer); timer = setTimeout(tick, 400); };
  const stop = () => { active = false; clearTimeout(timer); input.placeholder = 'Zoek naar…'; };
  input.addEventListener('focus', stop);
  input.addEventListener('blur', () => { if (!input.value) start(); });
  start();
})();

// Scroll reveal (mirrors storefront). Offset only applied after JS marks the
// document, so no-JS renders show everything. Reduced motion reveals instantly.
(() => {
  const els = document.querySelectorAll('.reveal');
  if (!els.length) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  document.documentElement.classList.add('js-reveal');
  const io = new IntersectionObserver((entries, obs) => {
    entries.forEach((e) => {
      if (e.isIntersecting) { e.target.setAttribute('data-revealed', ''); obs.unobserve(e.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
  els.forEach((el) => io.observe(el));
})();

// Product rail: arrow scrolling + edge-aware disabled state (storefront ProductRail).
const initRail = (track) => {
  // Scope voor de pijlen: normale rails staan in een .container, maar een rail
  // in de blogtekst niet. Zo'n blok markeert zichzelf met data-rail-scope, zodat
  // twee rails op één pagina elkaars knoppen niet oppikken.
  const scope = track.closest('[data-rail-scope]') || track.closest('.container');
  const tools = scope?.querySelector('.pr-tools');
  const prev = tools?.querySelector('[data-rail-prev]');
  const next = tools?.querySelector('[data-rail-next]');
  if (!prev || !next) return;
  // Eerst alles lezen, dan schrijven: disabled zetten tussen twee lezingen dwingt een reflow af.
  const update = () => {
    const x = track.scrollLeft;
    const max = track.scrollWidth - track.clientWidth;
    prev.disabled = x <= 4;
    next.disabled = x >= max - 4;
  };
  const step = (dir) => track.scrollBy({ left: dir * track.clientWidth * 0.85, behavior: 'smooth' });
  prev.addEventListener('click', () => step(-1));
  next.addEventListener('click', () => step(1));
  track.addEventListener('scroll', update, { passive: true });
  let updRaf = false;
  window.addEventListener('resize', () => {
    if (updRaf) return;
    updRaf = true;
    requestAnimationFrame(() => { updRaf = false; update(); });
  });
  requestAnimationFrame(update);
};
document.querySelectorAll('[data-rail-track]').forEach(initRail);

// Auto best sellers: a Producten-rail without a collection renders an empty slot,
// which is filled from the best-selling sort of /collections/all (Liquid cannot
// sort `collections.all`, so the order has to come from a collection URL).
//
// Cached like mega-nav, not the mobile drawer: the rail cards carry price +
// compare_at_price and are stock-filtered, and Easy:Sale rewrites compare on the
// storefront, so a session-lifetime copy could advertise a stale discount. Hence
// the same 5-min TTL, later pages in a browse skip the refetch, a price change
// still surfaces fast.
const RAILBEST_TTL_MS = 5 * 60 * 1000;
document.querySelectorAll('[data-rail-autobest]').forEach(async (slot) => {
  const msg = slot.querySelector('.pr-empty');
  const fallback = msg?.textContent;
  if (msg) msg.textContent = 'Bestsellers laden…';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  try {
    let html = sjCache.get('railbest', RAILBEST_TTL_MS);
    const fromCache = html != null;
    if (!fromCache) {
      const res = await fetch(`${root}collections/all?sort_by=best-selling&view=rail`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      html = await res.text();
    }
    const items = new DOMParser()
      .parseFromString(`<ul>${html}</ul>`, 'text/html')
      .querySelectorAll('li.pr-item');
    if (!items.length) throw new Error('no products');
    // Cache only a verified non-empty response, and only when freshly fetched, so
    // a bad 200 can't poison the rail for the whole TTL.
    if (!fromCache) sjCache.set('railbest', html);
    const track = document.createElement('ul');
    track.className = 'pr-track';
    track.setAttribute('data-rail-track', '');
    const limit = parseInt(slot.dataset.railCount, 10) || items.length;
    [...items].slice(0, limit).forEach((li) => track.appendChild(li));
    slot.replaceWith(track);
    initRail(track);
    document.dispatchEvent(new CustomEvent('sj:content-replaced', { detail: { container: track } }));
  } catch {
    if (msg && fallback) msg.textContent = fallback;
  }
});

// De verlanglijst zat hier ooit ook (key `sj.wish`, per product). Die tweede
// implementatie luisterde op dezelfde [data-wish]-knoppen als assets/wishlist.js
// en schreef naar een eigen store, waardoor het hartje en de teller uit elkaar
// liepen. Alles staat nu in assets/wishlist.js, key `sj.wishlist`, per variant.

// Mega dropdown. The panels with all category links are server-rendered in the
// header (crawlers need those links in the raw HTML). The featured product cards
// are not: the full panels come from the Section Rendering API (mega-nav) and
// replace the server panels, keeping the per-pillar product scan off every
// page's server render.
(() => {
  const bar = document.querySelector('[data-mega-bar]');
  if (!bar) return;

  // Reveal the sub panel of the hovered/focused hoofdcategorie within one panel.
  const initPanel = (mega) => {
    const cats = mega.querySelectorAll('[data-mega-cat]');
    const subs = mega.querySelectorAll('[data-mega-sub]');
    const show = (i) => {
      cats.forEach((c) => c.classList.toggle('mega-cat--on', c.dataset.megaCat === i));
      subs.forEach((s) => s.classList.toggle('hidden', s.dataset.megaSub !== i));
    };
    cats.forEach((c) => {
      c.addEventListener('mouseenter', () => show(c.dataset.megaCat));
      c.addEventListener('focus', () => show(c.dataset.megaCat));
    });
    mega.addEventListener('mouseleave', () => show('0'));
  };

  // Open/close a nav-item's dropdown with a grace period. The gap between the
  // link and the panel would otherwise kill the menu mid-travel; the timer keeps
  // it open ~250ms and any re-entry (link, gap bridge or panel) cancels close.
  bar.querySelectorAll('[data-mega-for]').forEach(initPanel);

  bar.querySelectorAll('.nav-item[data-mega-index]').forEach((item) => {
    let closeTimer = null;
    const open = () => {
      clearTimeout(closeTimer);
      item.classList.add('mega-open');
    };
    const close = () => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => item.classList.remove('mega-open'), 250);
    };
    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', close);
    item.addEventListener('focusin', open);
    item.addEventListener('focusout', close);
  });

  // The panel HTML is identical on every page, so cache it in sessionStorage:
  // fetch once, then every later page (and every later hover) slots panels in
  // from cache with no network. A warm-up on idle primes this so even the first
  // hover is usually already instant.
  //
  // Unlike the mobile drawer this markup is NOT static: the CTA card carries the
  // featured product's price + compare_at_price, and the product itself is picked
  // by stock (see mega-nav.liquid). Easy:Sale rewrites compare_at_price on the
  // storefront, so a session-lifetime copy can advertise a discount that no
  // longer exists. Hence the TTL, long enough that a browsing session pays for
  // at most a handful of renders, short enough that a price change surfaces fast.
  const MEGA_TTL_MS = 5 * 60 * 1000;
  let applied = false;
  let loading = false;

  const applyHTML = (html) => {
    if (applied) return;
    applied = true;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const root = tmp.querySelector('#shopify-section-mega-nav') || tmp;
    root.querySelectorAll('[data-mega-for]').forEach((panel) => {
      const item = bar.querySelector(`.nav-item[data-mega-index="${panel.dataset.megaFor}"]`);
      if (!item) return;
      item.querySelector('[data-mega]')?.remove(); // server panel without cards
      item.appendChild(panel);
      initPanel(panel);
    });
  };

  const loadPanels = async () => {
    if (applied || loading || !bar.dataset.src) return;
    const cached = sjCache.get('mega-nav', MEGA_TTL_MS);
    if (cached) { applyHTML(cached); return; }
    loading = true;
    try {
      const res = await fetch(bar.dataset.src, { headers: { 'X-Requested-With': 'fetch' } });
      if (!res.ok) throw new Error(res.status);
      const html = await res.text();
      sjCache.set('mega-nav', html);
      applyHTML(html);
    } catch (e) {
      loading = false; // let a later hover retry (server panel stays until then)
    }
  };

  bar.addEventListener('mouseenter', loadPanels);
  bar.addEventListener('focusin', loadPanels);
  // Prime once the page is idle: instant from cache on later pages, and even the
  // first page's first hover is usually ready before the pointer arrives.
  if ('requestIdleCallback' in window) requestIdleCallback(() => loadPanels(), { timeout: 2000 });
  else setTimeout(loadPanels, 800);
})();

// Footer. The link columns and the legal block are server-rendered, internal
// links belong in the page source, but the brand block, the USP bar and the
// payment icons are not: they hold no links and no price, stock, cart or
// customer state, they are most of the footer's bytes (payment_type_svg_tag
// inlines a full SVG per method) and they are byte-identical on every page.
// Fetch that half once per session and slot it in from sessionStorage after.
// No TTL for the same reason the mobile drawer has none: nothing in this markup
// can go stale within a session that the sjCache key doesn't already cover.
(() => {
  const footer = document.querySelector('[data-footer-src]');
  const slots = footer ? footer.querySelectorAll('[data-footer-slot]') : [];
  if (!slots.length) return;
  // A newsletter signup comes back as ?customer_posted=true and the form's
  // success message is rendered off that flag, so pass it through on that one
  // request and keep the response out of the cache.
  const posted = new URLSearchParams(location.search).get('customer_posted') === 'true';
  const src = posted ? `${footer.dataset.footerSrc}&customer_posted=true` : footer.dataset.footerSrc;

  let done = false;
  const apply = (html) => {
    if (done) return;
    done = true;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    slots.forEach((slot) => {
      const part = tmp.querySelector(`[data-footer-part="${slot.dataset.footerSlot}"]`);
      if (part) slot.innerHTML = part.innerHTML;
    });
  };

  let loading = false;
  const loadFooter = () => {
    if (done || loading) return;
    const cached = posted ? null : sjCache.get('footer');
    if (cached) { apply(cached); return; }
    loading = true;
    fetch(src, { headers: { 'X-Requested-With': 'fetch' } })
      .then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then((html) => {
        if (!posted) sjCache.set('footer', html);
        apply(html);
      })
      .catch(() => { loading = false; }); // reserved space stays; a later trigger retries
  };

  // Whichever comes first: the footer nearing the viewport, or the page going
  // idle. From the second page on the cache makes this synchronous anyway.
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      loadFooter();
    }, { rootMargin: '600px' });
    io.observe(footer);
  }
  if ('requestIdleCallback' in window) requestIdleCallback(() => loadFooter(), { timeout: 3000 });
  else setTimeout(loadFooter, 1000);
})();

// ── Newsletter → Klaviyo ──
//
// The two newsletter forms are plain {% form 'customer' %} tags, so without JS
// they still do their old Shopify post. When theme.liquid emits window.__kl
// (both the public key and the list id are filled in) we intercept the submit
// and write straight to Klaviyo's Client-side Subscribe API instead, which puts
// the address in the list immediately rather than waiting for the Shopify sync.
//
// The listener is delegated because the footer form does not exist at page load:
// it arrives with the deferred footer fragment above, or straight out of
// sessionStorage on the second page view.
(() => {
  const cfg = window.__kl;
  if (!cfg || !cfg.k || !cfg.l) return;

  // Klaviyo pins its API shape to a dated revision; this is the one the payload
  // below is written against.
  const REVISION = '2024-10-15';

  const show = (el, text) => {
    if (!el) return;
    if (text != null) el.textContent = text;
    el.hidden = false;
  };

  const succeed = (form) => {
    form.querySelector('[data-kl-fields]')?.setAttribute('hidden', '');
    form.querySelector('[data-kl-error]')?.setAttribute('hidden', '');
    show(form.querySelector('[data-kl-done]'));
  };

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-kl-newsletter]');
    if (!form) return;

    const email = form.querySelector('input[type="email"]')?.value.trim();
    if (!email) return; // let the browser's own required/type validation speak

    e.preventDefault();

    // Bots fill every field they find. Show them the success state and post
    // nothing, so they cannot tell the difference and retry.
    if (form.querySelector('input[name="nl_hp"]')?.value) { succeed(form); return; }

    const source = form.dataset.klNewsletter;
    const error = form.querySelector('[data-kl-error]');
    const button = form.querySelector('button[type="submit"]');
    if (error) error.hidden = true;
    if (button) button.disabled = true;

    fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(cfg.k)}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: source,
            profile: {
              data: {
                type: 'profile',
                attributes: { email, properties: { signup_source: source } },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: cfg.l } } },
        },
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.status); // 202 + empty body on success
        succeed(form);
        // Ties this browser session to the profile, so onsite tracking and the
        // flows that read it know who is browsing from here on. The push form
        // works both before and after the onsite embed finishes loading, until
        // then window.klaviyo is still the plain queue array.
        if (window.klaviyo) window.klaviyo.push(['identify', { $email: email }]);
      })
      .catch(() => {
        show(error, cfg.e);
        if (button) button.disabled = false;
      });
  });
})();

// ── A11y-patch voor de Alpha Loyalty-widget ──
//
// De app injecteert een launcher-knop zonder toegankelijke naam en een
// e-mailveld zonder label. Beide staan in app-DOM, dus ze zijn niet vanuit dit
// theme te herschrijven, maar een screenreader (en Lighthouse) rekent ze wel
// aan deze pagina toe. Deze patch zet alleen de ontbrekende attributen; hij
// raakt niets aan wat de app zelf al invult.
(() => {
  const FIXES = [
    { sel: '.AlLo-badge', label: 'PleasurePoints openen' },
    { sel: '#alpha_loyalty--subscription_form input[type="email"]', label: 'E-mailadres' },
  ];

  let observer = null;
  let timer = 0;
  const stop = () => {
    observer?.disconnect();
    clearTimeout(timer);
  };

  // true zodra elk doelwit gevonden en gelabeld is, zodat de observer kan stoppen.
  const sweep = () =>
    FIXES.every(({ sel, label }) => {
      const el = document.querySelector(sel);
      if (!el) return false;
      if (!el.getAttribute('aria-label') && !el.getAttribute('aria-labelledby')) {
        el.setAttribute('aria-label', label);
      }
      return true;
    });

  if (sweep()) return;
  observer = new MutationObserver(() => {
    if (sweep()) stop();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  // De widget laadt async. Is hij er na 20s niet, dan komt hij niet meer en
  // hoeft de observer niet de hele sessie mee te draaien.
  timer = setTimeout(stop, 20000);
})();
